/**
 * Error reporting (Sentry, EU region). The SDK is its own chunk, loaded only when VITE_SENTRY_DSN
 * is set; its default integrations report uncaught errors and unhandled rejections. No personal
 * data is sent (`dataCollection`, Sentry 11's successor of sendDefaultPii: no IP, user, cookies,
 * headers or bodies), so it needs no consent. `urlQueryParams` only covers what Sentry filters
 * itself: the page URL and the breadcrumbs (navigations, requests) keep their query, where search
 * words, emails in PocketBase filters and file tokens live, so `scrubEvent`/`scrubBreadcrumb` cut
 * it off. Nothing is sent per page load (no session tracking), only when an error occurs. The app
 * has no error boundary: React 19 hands render errors to window.reportError, which Sentry's
 * global handler picks up.
 */
import type { Breadcrumb, ErrorEvent } from "@sentry/browser";
import { env } from "@/lib/core/env";

export const NO_PERSONAL_DATA = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
};

/** The URL without its query and fragment. */
export const withoutQuery = (url: string) => url.replace(/[?#][\s\S]*$/, "");

/** Navigation (`from`, `to`) and request (`url`) breadcrumbs keep only the path. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data;
  for (const key of ["url", "from", "to"]) {
    if (typeof data?.[key] === "string") data[key] = withoutQuery(data[key]);
  }
  return breadcrumb;
}

/** The page URL and referrer of a report keep only the path. */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  const request = event.request;
  if (request?.url) request.url = withoutQuery(request.url);
  if (request) delete request.query_string;
  if (request?.headers?.Referer) request.headers.Referer = withoutQuery(request.headers.Referer);
  return event;
}

/** Call once at startup (main.tsx). A failed download only means no reports. */
export async function initErrorReporting(): Promise<void> {
  if (!env.sentryDsn) return;
  try {
    const Sentry = await import("@sentry/browser");
    Sentry.init({
      dsn: env.sentryDsn,
      release: env.appVersion,
      environment: env.deployEnv || undefined,
      dataCollection: NO_PERSONAL_DATA,
      // A session is sent on every page load otherwise.
      integrations: (defaults) => defaults.filter(({ name }) => name !== "BrowserSession"),
      beforeBreadcrumb: scrubBreadcrumb,
      beforeSend: scrubEvent,
    });
  } catch {
    // Offline or blocked: the app works the same without it.
  }
}
